'use client';

import { c as _c } from "react/compiler-runtime";
import { ChartLoading } from '@/components/Charts/ChartsLoading';
import dayjs from 'dayjs';
import { CandlestickSeries, ColorType, type IChartApi, createChart } from 'lightweight-charts';
import { chartTheme as theme } from '@/constants/project-specific/theme/charts';
import { type Dispatch, type SetStateAction, useEffect, useRef, useState } from 'react';
const CANDLE_CHART_HEIGHT = 250;
export type LineChartProps = {
  data: any[] | null | undefined;
  setValue?: Dispatch<SetStateAction<number | undefined>>; // used for value on hover
  setLabel?: Dispatch<SetStateAction<string | undefined>>; // used for value label on hover
} & React.HTMLAttributes<HTMLDivElement>;
const StakingChart = t0 => {
  const $ = _c(10);
  const {
    data,
    setValue,
    setLabel
  } = t0;
  const chartRef = useRef(null);
  const [chartCreated, setChart] = useState();
  let t1;
  let t2;
  if ($[0] !== data || $[1] !== setLabel || $[2] !== setValue) {
    t1 = () => {
      if (!chartRef.current || !data) {
        return;
      }
      const chart = createChart(chartRef.current, {
        autoSize: true,
        layout: {
          attributionLogo: false,
          background: {
            type: ColorType.Solid,
            color: "transparent"
          },
          textColor: theme.text.color.textSubtle,
          fontFamily: theme.text.fontFamily,
          fontSize: theme.text.fontSize
        },
        rightPriceScale: {
          visible: false,
          scaleMargins: {
            top: 0.1,
            bottom: 0.1
          },
          borderVisible: false
        },
        grid: {
          horzLines: {
            visible: false
          },
          vertLines: {
            visible: false
          }
        },
        crosshair: {
          horzLine: {
            visible: false,
            labelVisible: false
          },
          mode: 1,
          vertLine: {
            visible: false,
            labelVisible: false,
            style: 3,
            width: 1,
            color: theme.text.color.textSubtle,
            labelBackgroundColor: theme.text.color.text
          }
        }
      });
      chart.timeScale().fitContent();
      setChart(chart);
      const series = chart.addSeries(CandlestickSeries, {
        upColor: theme.candleStick.upColor,
        downColor: theme.candleStick.downColor,
        borderDownColor: theme.candleStick.borderDownColor,
        borderUpColor: theme.candleStick.borderUpColor,
        wickDownColor: theme.candleStick.wickDownColor,
        wickUpColor: theme.candleStick.wickUpColor
      });
      series.setData(data);
      chart.applyOptions({
        layout: {
          textColor: theme.text.color.text
        }
      });
      chart.subscribeCrosshairMove(param => {
        if (chartRef?.current && (param === undefined || param.time === undefined || param && param.point && param.point.x < 0 || param && param.point && param.point.x > chartRef.current.clientWidth || param && param.point && param.point.y < 0 || param && param.point && param.point.y > CANDLE_CHART_HEIGHT)) {
          if (setValue) {
            setValue(undefined);
          }
          if (setLabel) {
            setLabel(undefined);
          }
        } else {
          if (series && param) {
            const timestamp = param.time as number;
            const now = new Date(timestamp * 1000);
            const time = `${now.toLocaleString("en-us", {
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "numeric",
              minute: "2-digit",
              timeZone: "UTC"
            })} (UTC)`;
            const parsed = param.seriesData.get(series) as {
              open: number;
            } | undefined;
            if (setValue) {
              setValue(parsed?.open);
            }
            if (setLabel) {
              setLabel(time);
            }
          }
        }
      });
      return () => {
        if (chart) {
          chart.remove();
          setChart(undefined);
        }
      };
    };
    t2 = [data, setLabel, setValue];
    $[0] = data;
    $[1] = setLabel;
    $[2] = setValue;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  if ($[5] !== chartCreated) {
    t3 = !chartCreated && <ChartLoading />;
    $[5] = chartCreated;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="flex flex-col content-center items-center self-center w-full h-full z-0"><div className="flex w-full h-full z-0" ref={chartRef} /></div>;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t3) {
    t5 = <>{t3}{t4}</>;
    $[8] = t3;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
};
export default StakingChart;